import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QuotationsListColumns } from "../../../helpers/adminConstants/TableConstants";
import { QuotationsWrapper } from "./styled";
import {
  Box,
  ButtonStyledIconLeft,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import {
  PlusIcon,
  QuotationsIcon,
  SearchIcon,
} from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import QuotationsApi from "../../../services/api/quotationsApi";
import { useDebounce } from "../../../hooks/useDebounce";
import { toast } from "react-toastify";
import Invoice from "../../../components/common/pdfInvoice/PDFInvoice";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { generateQuotationPDF } from "../../../utils/generationPdf";
import { useUser } from "../../../context/userContext";
import { getModuleOperations, toBase64 } from "../../../utils";

const Quotations = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [logoBase64, setLogoBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [quotationId, setQuotationId] = useState(null);
  const [rowsData, setRowsData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [dataObj, setDataObj] = useState(null);

  useEffect(() => {
    if (user?.organization?.logo) {
      toBase64(
        `${process.env.REACT_APP_ASSET_URL}/${user?.organization?.logo}`
      ).then(setLogoBase64);
    }
  }, []);

  const onDelete = (row) => {
    setQuotationId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (quotation) => {
    navigate(`/quotations/${quotation?.id}/view-quotation`);
  };

  const handleEdit = (quotation) => {
    navigate(`/quotations/${quotation?.id}/edit-quotation`);
  };

  const handleDownload = async (row) => {
    const data = await fetchQuotationDetails(row?.id);
    await generateQuotationPDF(data, logoBase64, "quotation.pdf");
  };
  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const handleAdd = async () => {
    try {
      setCheckingLimit(true);
      const response = await QuotationsApi.checkQuotationLimit();
      if (response?.data?.can_create) navigate("/quotations/create-quotation");
      else if (response?.message) toast.error(response?.message);
    } catch (error) {
    } finally {
      setCheckingLimit(false);
    }
  };

  const fetchQuotationDetails = async (id) => {
    try {
      const { data, code } = await QuotationsApi.viewQuotation(id);
      if (code === 200 && data) {
        setDataObj(data);
        return data;
      }
    } catch (error) {
      toast.error(error?.message);
      return null;
    } finally {
    }
  };

  const getQuotationsList = async (page = 1, query = "") => {
    try {
      setLoading(true);
      const { data, code } = await QuotationsApi.quotationsList(page, query);

      if (code === 200) {
        const updatedData = data?.data?.map((item) => ({
          id: item.id,
          name: item.name,
          quotation_no: item.quotation_no,
          client_name:
            language === "en"
              ? item.client_name_en || item.client_name_ar
              : item.client_name_ar || item.client_name_en,
          discount_type: item.discount_type,
          discount: item.discount,
          taxable_amount: item.taxable_amount,
          tax_percentage: item.tax_percentage,
          amount: item.total_amount,
          notes: item.notes,
          date: item.date,
        }));

        setRowsData(updatedData);
        setTotalCount(data?.meta?.total);
        setPerPage(data?.meta?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await QuotationsApi.deleteQuotation(quotationId);
      if (response.code === 200) {
        toast.success(t("quotation_deleted_successfully"));
        setShowDeleteModal(false);
        getQuotationsList(activePage);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  useEffect(() => {
    getQuotationsList(activePage, debouncedSearchQuery);
  }, [activePage, debouncedSearchQuery, language]);

  return (
    <QuotationsWrapper>
      <Box className="content-head">
        <Box className="w-fit d-flex align-items-center gap-3">
          <QuotationsIcon />
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("quotations")}
          </SubHeadingStyled>
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            icon={SearchIcon}
            onChange={handleSearch}
          />

          {getModuleOperations(4, 1) && (
            <ButtonStyledIconLeft
              label={t("create_quotation")}
              icon={PlusIcon}
              isLoading={checkingLimit}
              disabled={checkingLimit}
              onClick={handleAdd}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            typeId={4}
            options={["view", "download", "delete"]}
            rows={rowsData}
            columns={QuotationsListColumns}
            onDelete={onDelete}
            onOpen={handleView}
            onEdit={handleEdit}
            onDownload={handleDownload}
            // onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={totalCount}
            perPage={perPage}
          />
        )}
      </Box>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("quotation")}
        isLoading={loadingDelete}
      />
      {/* {dataObj && (
        <Invoice quotationData={dataObj} userData={user} logo={logoBase64} />
      )} */}
    </QuotationsWrapper>
  );
};

export default Quotations;
